import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  ShieldCheck,
  RefreshCw,
  Download,
  AlertTriangle,
  Banknote,
  FileText,
  TrendingUp,
  Percent,
  Wallet,
  Inbox,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { DatePicker, message } from "antd";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import "antd/dist/reset.css";
import { useTheme } from "../context/ThemeContext";
import { usePermissions } from "../context/PermissionContext";
import { fintechApi } from "../services/api";

const { RangePicker } = DatePicker;

const formatINR = (val) => {
  if (val === undefined || val === null || isNaN(val)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

const formatCount = (val) => {
  if (val === undefined || val === null || isNaN(val)) return "0";
  return new Intl.NumberFormat("en-IN").format(val);
};

// Percent value ko safe string me badalta hai (API "12.5" ya 12.5 dono bhej sakta hai)
const formatPct = (val) => {
  if (val === undefined || val === null || val === "") return "—";
  const num = typeof val === "string" ? parseFloat(val.replace("%", "")) : Number(val);
  if (isNaN(num)) return "—";
  return `${num.toFixed(2)}%`;
};

const num = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && v !== "" && !isNaN(Number(v))) return Number(v);
  }
  return 0;
};

const pick = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
};

// RBI IRAC classification ka fixed display order
const NPA_ORDER = ["STANDARD", "SMA-0", "SMA-1", "SMA-2", "NPA"];

const NPA_STYLE = {
  STANDARD: "bg-emerald-50 text-emerald-700",
  "SMA-0": "bg-lime-50 text-lime-700",
  "SMA-1": "bg-amber-50 text-amber-700",
  "SMA-2": "bg-orange-50 text-orange-700",
  NPA: "bg-red-50 text-red-700",
};

// "sma 1", "SMA_1", "sma1" jaisi sab keys ko ek hi canonical form me lao
const normalizeClassification = (raw) => {
  if (raw === undefined || raw === null) return "";
  const key = String(raw).toUpperCase().replace(/[\s_]+/g, "");
  if (key === "SMA0") return "SMA-0";
  if (key === "SMA1") return "SMA-1";
  if (key === "SMA2") return "SMA-2";
  if (key === "SMA-0" || key === "SMA-1" || key === "SMA-2") return key;
  if (key === "STANDARD" || key === "STD") return "STANDARD";
  if (key === "NPA" || key === "SUBSTANDARD" || key === "SUB-STANDARD") return key === "NPA" ? "NPA" : key;
  return String(raw).toUpperCase().replace(/[\s_]+/g, "-");
};

export default function RbiComplianceReport() {
  const { currentTheme } = useTheme();
  const primaryColor = currentTheme?.color || "#2e856e";
  const { has } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Default range = current month (month picker)
  const [dateRange, setDateRange] = useState([dayjs().startOf("month"), dayjs().endOf("month")]);

  const startDate = dateRange?.[0] ? dateRange[0].startOf("month").format("YYYY-MM-DD") : null;
  const endDate = dateRange?.[1] ? dateRange[1].endOf("month").format("YYYY-MM-DD") : null;

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fintechApi.getRbiReport(startDate, endDate);
      // Response shape tolerant — kabhi {data:{...}} kabhi seedha object
      const data = res?.data || res;
      if (!data || typeof data !== "object") {
        throw new Error("Report service returned an unexpected response");
      }
      setReport(data);
    } catch (err) {
      console.error("RBI compliance report fetch failed:", err);
      // API fail hua to koi success/mock data nahi — sirf honest error
      setReport(null);
      setError(err?.message || "Failed to load RBI compliance report");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // ── Section 1: Disbursement Summary ──
  const disbursement = useMemo(() => {
    const d = report?.disbursementSummary || report?.disbursement || report?.disbursements || {};
    const rawRows = pick(d.loanTypeWise, d.byLoanType, d.loanTypes, d.breakdown, d.rows, report?.loanTypeWise);
    const rows = Array.isArray(rawRows)
      ? rawRows
      : rawRows && typeof rawRows === "object"
      ? Object.entries(rawRows).map(([k, v]) => ({
          loanType: k,
          ...(v && typeof v === "object" ? v : { amount: v }),
        }))
      : [];
    return {
      totalLoans: num(d.totalLoansDisbursed, d.totalLoans, d.loanCount, d.count, d.totalCount),
      totalAmount: num(d.totalAmountDisbursed, d.totalAmount, d.amount, d.disbursedAmount),
      rows: rows.map((r) => ({
        loanType: pick(r.loanType, r.type, r.name, r.productType, r.product) || "—",
        count: num(r.count, r.loanCount, r.totalLoans, r.loans),
        amount: num(r.amount, r.totalAmount, r.disbursedAmount, r.value),
      })),
    };
  }, [report]);

  // ── Section 2: NPA Summary ──
  const npaRows = useMemo(() => {
    const src = report?.npaSummary || report?.npa || report?.assetClassification || {};
    let list = [];
    if (Array.isArray(src)) list = src;
    else if (Array.isArray(src.rows)) list = src.rows;
    else if (Array.isArray(src.classifications)) list = src.classifications;
    else if (src && typeof src === "object") {
      list = Object.entries(src)
        .filter(([, v]) => v && typeof v === "object")
        .map(([k, v]) => ({ classification: k, ...v }));
    }

    const mapped = list.map((r) => ({
      classification: normalizeClassification(
        pick(r.classification, r.category, r.assetClass, r.name, r.label, r.type)
      ),
      count: num(r.count, r.accounts, r.totalAccounts, r.loans, r.noOfAccounts),
      amount: num(r.amount, r.totalAmount, r.outstanding, r.outstandingAmount, r.value),
      percentage: pick(r.percentage, r.percent, r.pct, r.share, r.ratio),
    }));

    const byKey = new Map();
    mapped.forEach((r) => {
      if (r.classification) byKey.set(r.classification, r);
    });

    // Fixed RBI order pehle, uske baad API ki koi extra classification
    const ordered = NPA_ORDER.filter((k) => byKey.has(k)).map((k) => byKey.get(k));
    const extras = mapped.filter((r) => r.classification && !NPA_ORDER.includes(r.classification));
    return [...ordered, ...extras];
  }, [report]);

  // ── Section 3: Collection Efficiency ──
  const collection = useMemo(() => {
    const c =
      report?.collectionEfficiency || report?.collection || report?.collections || {};
    const rawTrend = pick(c.monthlyTrend, c.trend, c.monthly, c.months, c.breakdown, report?.collectionTrend);
    const trendList = Array.isArray(rawTrend)
      ? rawTrend
      : rawTrend && typeof rawTrend === "object"
      ? Object.entries(rawTrend).map(([k, v]) => ({
          month: k,
          ...(v && typeof v === "object" ? v : { collected: v }),
        }))
      : [];

    const totalDue = num(c.totalDue, c.totalDemand, c.demand, c.due);
    const totalCollected = num(c.totalCollected, c.collected, c.collectionAmount);
    const rawEff = pick(c.efficiency, c.efficiencyPercentage, c.efficiencyRate, c.collectionEfficiency);
    const efficiency =
      rawEff !== undefined
        ? rawEff
        : totalDue > 0
        ? (totalCollected / totalDue) * 100
        : undefined;

    return {
      totalDue,
      totalCollected,
      efficiency,
      trend: trendList.map((t) => ({
        month: String(pick(t.month, t.period, t.monthName, t.label, t.name) || "—"),
        due: num(t.due, t.totalDue, t.demand, t.totalDemand),
        collected: num(t.collected, t.totalCollected, t.collection, t.amount),
        efficiency: pick(t.efficiency, t.efficiencyPercentage, t.efficiencyRate),
      })),
    };
  }, [report]);

  // ── Section 4: Interest Income ──
  const interest = useMemo(() => {
    const i = report?.interestIncome || report?.interest || {};
    const rawRows = pick(i.monthWise, i.monthlyBreakdown, i.monthly, i.breakdown, i.months, i.trend, report?.interestBreakdown);
    const list = Array.isArray(rawRows)
      ? rawRows
      : rawRows && typeof rawRows === "object"
      ? Object.entries(rawRows).map(([k, v]) => ({
          month: k,
          ...(v && typeof v === "object" ? v : { interest: v }),
        }))
      : [];
    return {
      totalInterest: num(i.totalInterestEarned, i.totalInterest, i.total, i.amount, i.interestEarned),
      rows: list.map((r) => ({
        month: String(pick(r.month, r.period, r.monthName, r.label, r.name) || "—"),
        interest: num(r.interest, r.interestEarned, r.amount, r.totalInterest, r.value),
        principal: pick(r.principal, r.principalCollected),
      })),
    };
  }, [report]);

  const hasAnyData =
    !!report &&
    (disbursement.totalLoans > 0 ||
      disbursement.totalAmount > 0 ||
      disbursement.rows.length > 0 ||
      npaRows.length > 0 ||
      collection.totalDue > 0 ||
      collection.totalCollected > 0 ||
      collection.trend.length > 0 ||
      interest.totalInterest > 0 ||
      interest.rows.length > 0);

  const periodLabel =
    dateRange?.[0] && dateRange?.[1]
      ? `${dateRange[0].startOf("month").format("DD/MM/YYYY")} - ${dateRange[1]
          .endOf("month")
          .format("DD/MM/YYYY")}`
      : "—";

  // ── Excel export — sab kuch loaded data se hi banta hai, koi dummy row nahi ──
  const handleDownloadExcel = () => {
    if (!hasAnyData) return;
    setDownloading(true);
    try {
      const wb = XLSX.utils.book_new();

      const disbSheet = [
        { Metric: "Report Period", Value: periodLabel },
        { Metric: "Total Loans Disbursed", Value: disbursement.totalLoans },
        { Metric: "Total Amount Disbursed (INR)", Value: disbursement.totalAmount },
        {},
        { Metric: "Loan Type", Value: "Count", Amount: "Amount (INR)" },
        ...disbursement.rows.map((r) => ({
          Metric: r.loanType,
          Value: r.count,
          Amount: r.amount,
        })),
      ];
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(disbSheet, { header: ["Metric", "Value", "Amount"] }),
        "Disbursement"
      );

      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(
          npaRows.map((r) => ({
            Classification: r.classification,
            Count: r.count,
            "Amount (INR)": r.amount,
            "%": formatPct(r.percentage),
          })),
          { header: ["Classification", "Count", "Amount (INR)", "%"] }
        ),
        "NPA Summary"
      );

      const colSheet = [
        { Month: "TOTAL", "Due (INR)": collection.totalDue, "Collected (INR)": collection.totalCollected, "Efficiency %": formatPct(collection.efficiency) },
        ...collection.trend.map((t) => ({
          Month: t.month,
          "Due (INR)": t.due,
          "Collected (INR)": t.collected,
          "Efficiency %": formatPct(t.efficiency !== undefined ? t.efficiency : t.due > 0 ? (t.collected / t.due) * 100 : undefined),
        })),
      ];
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(colSheet, { header: ["Month", "Due (INR)", "Collected (INR)", "Efficiency %"] }),
        "Collection Efficiency"
      );

      const intSheet = [
        { Month: "TOTAL", "Interest Earned (INR)": interest.totalInterest },
        ...interest.rows.map((r) => ({
          Month: r.month,
          "Interest Earned (INR)": r.interest,
        })),
      ];
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.json_to_sheet(intSheet, { header: ["Month", "Interest Earned (INR)"] }),
        "Interest Income"
      );

      XLSX.writeFile(
        wb,
        `RBI_Compliance_Report_${startDate || "NA"}_to_${endDate || "NA"}.xlsx`
      );
      message.success("RBI compliance report exported successfully!");
    } catch (err) {
      console.error("RBI report export failed:", err);
      message.error("Export failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  // Chhota reusable empty state — section ke andar
  const SectionEmpty = ({ text }) => (
    <div className="py-10 text-center text-gray-400">
      <Inbox className="w-8 h-8 mx-auto mb-2 opacity-30" />
      <p className="text-xs font-semibold">{text}</p>
    </div>
  );

  // Export gate — admin ko PermissionContext khud bypass karwa deta hai
  const canExport = has("FINTECH_REPORT_VIEW") || has("REPORT_EXPORT");

  return (
    <div className="space-y-6 pb-12">
      {/* ── Page Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-[#e5e7eb] shadow-sm">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-md"
            style={{ backgroundColor: primaryColor }}
          >
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
              RBI Compliance Report
            </h1>
            <p className="text-xs md:text-sm text-gray-500">
              Regulatory disbursement, asset classification, recovery and interest income statement
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <RangePicker
            picker="month"
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            format="MMM YYYY"
            allowClear={false}
            placeholder={["Start Month", "End Month"]}
            className="rounded-xl border-gray-300 py-2 shadow-sm font-medium"
          />

          <button
            onClick={fetchReport}
            disabled={loading}
            className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl border border-gray-200 transition-all shadow-sm"
            title="Refresh Report"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>

          {canExport && (
            <button
              onClick={handleDownloadExcel}
              disabled={!hasAnyData || loading || downloading}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#2e856e] text-white text-sm font-semibold rounded-xl shadow-md transition-all hover:brightness-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100"
              style={hasAnyData && !loading ? { backgroundColor: primaryColor } : undefined}
              title={hasAnyData ? "Download Excel" : "No data available to export"}
            >
              <Download className="w-4 h-4" />
              <span>{downloading ? "Exporting..." : "Download Excel"}</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Reporting period strip ── */}
      <div className="bg-white px-5 py-3 rounded-2xl border border-[#e5e7eb] shadow-sm flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
        <span className="font-bold uppercase tracking-wider text-gray-500">Reporting Period</span>
        <span className="font-bold text-gray-900">{periodLabel}</span>
        <span className="text-gray-400">
          {loading ? "Fetching latest figures from server..." : error ? "Data unavailable" : hasAnyData ? "Figures as reported by the lending system" : "No records for this period"}
        </span>
      </div>

      {/* ── Loading State ── */}
      {loading && (
        <div className="bg-white p-12 rounded-2xl border border-[#e5e7eb] shadow-sm text-center">
          <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin text-[#2e856e]" />
          <p className="text-sm font-semibold text-gray-700">Loading RBI compliance report...</p>
          <p className="text-xs text-gray-400 mt-1">Please wait while we compile the regulatory figures</p>
        </div>
      )}

      {/* ── Error State (API fail) — yahan koi fake data nahi dikhega ── */}
      {!loading && error && (
        <div className="bg-white p-12 rounded-2xl border border-red-200 shadow-sm text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <p className="text-sm font-bold text-gray-900">Unable to load the RBI compliance report</p>
          <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto break-words">{error}</p>
          <button
            onClick={fetchReport}
            className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 bg-[#2e856e] text-white text-sm font-semibold rounded-xl shadow-md transition-all hover:brightness-105 active:scale-95"
            style={{ backgroundColor: primaryColor }}
          >
            <RefreshCw className="w-4 h-4" />
            <span>Retry</span>
          </button>
        </div>
      )}

      {/* ── Empty State (API ok par data nahi) ── */}
      {!loading && !error && !hasAnyData && (
        <div className="bg-white p-12 rounded-2xl border border-[#e5e7eb] shadow-sm text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-gray-50 text-gray-400 flex items-center justify-center">
            <Inbox className="w-6 h-6" />
          </div>
          <p className="text-sm font-bold text-gray-900">No compliance data for this period</p>
          <p className="text-xs text-gray-500 mt-1">
            Try selecting a different month range, or refresh once the ledger is updated.
          </p>
          <button
            onClick={fetchReport}
            className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 bg-[#2e856e] text-white text-sm font-semibold rounded-xl shadow-md transition-all hover:brightness-105 active:scale-95"
            style={{ backgroundColor: primaryColor }}
          >
            <RefreshCw className="w-4 h-4" />
            <span>Retry</span>
          </button>
        </div>
      )}

      {/* ── Report Body ── */}
      {!loading && !error && hasAnyData && (
        <>
          {/* ── Section 1: Disbursement Summary ── */}
          <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden">
            <div className="p-6 pb-4 flex items-center gap-3 border-b border-gray-100">
              <div className="p-2 bg-[#2e856e]/10 text-[#2e856e] rounded-xl">
                <Banknote className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">1. Disbursement Summary</h2>
                <p className="text-xs text-gray-500">Loans disbursed during the reporting period</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-6">
              <div className="bg-gradient-to-br from-white to-emerald-50/30 p-5 rounded-2xl border border-emerald-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase text-gray-500">Total Loans Disbursed</span>
                  <div className="p-2 bg-[#2e856e]/10 text-[#2e856e] rounded-xl">
                    <FileText className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {formatCount(disbursement.totalLoans)}
                </div>
                <p className="text-xs text-gray-500 mt-1">Total sanctioned files paid out</p>
              </div>

              <div className="bg-gradient-to-br from-white to-blue-50/30 p-5 rounded-2xl border border-blue-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase text-gray-500">Total Amount Disbursed</span>
                  <div className="p-2 bg-blue-100 text-blue-600 rounded-xl">
                    <Wallet className="w-5 h-5" />
                  </div>
                </div>
                <div className="text-2xl font-black text-gray-900">
                  {formatINR(disbursement.totalAmount)}
                </div>
                <p className="text-xs text-gray-500 mt-1">Gross payout value in the period</p>
              </div>
            </div>

            <div className="px-6 pb-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">
                Loan Type Wise Break-up
              </h3>
              {disbursement.rows.length === 0 ? (
                <SectionEmpty text="No loan-type break-up returned for this period" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50/75 text-gray-600 text-xs uppercase font-bold">
                        <th className="py-3.5 px-4">Loan Type</th>
                        <th className="py-3.5 px-4 text-center">Count</th>
                        <th className="py-3.5 px-4 text-right">Amount Disbursed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {disbursement.rows.map((row, idx) => (
                        <tr key={`${row.loanType}-${idx}`} className="hover:bg-gray-50/50 transition-colors">
                          <td className="py-3.5 px-4">
                            <span className="text-xs font-semibold px-2.5 py-1 rounded-lg bg-gray-100 text-gray-700">
                              {row.loanType}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 text-center font-medium text-gray-700">
                            {formatCount(row.count)}
                          </td>
                          <td className="py-3.5 px-4 text-right font-black text-gray-900">
                            {formatINR(row.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* ── Section 2: NPA Summary ── */}
          <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden">
            <div className="p-6 pb-4 flex items-center gap-3 border-b border-gray-100">
              <div className="p-2 bg-red-50 text-red-600 rounded-xl">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">2. NPA Summary</h2>
                <p className="text-xs text-gray-500">
                  Asset classification as per RBI IRAC norms (SMA / NPA)
                </p>
              </div>
            </div>

            {npaRows.length === 0 ? (
              <div className="px-6 pb-6">
                <SectionEmpty text="No asset classification data returned for this period" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/75 text-gray-600 text-xs uppercase font-bold">
                      <th className="py-3.5 px-4">Classification</th>
                      <th className="py-3.5 px-4 text-center">Count</th>
                      <th className="py-3.5 px-4 text-right">Amount</th>
                      <th className="py-3.5 px-4 text-right">%</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {npaRows.map((row, idx) => (
                      <tr key={`${row.classification}-${idx}`} className="hover:bg-gray-50/50 transition-colors">
                        <td className="py-3.5 px-4">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-bold ${
                              NPA_STYLE[row.classification] || "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {row.classification}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-center font-medium text-gray-700">
                          {formatCount(row.count)}
                        </td>
                        <td className="py-3.5 px-4 text-right font-bold text-gray-900">
                          {formatINR(row.amount)}
                        </td>
                        <td className="py-3.5 px-4 text-right font-bold text-gray-700">
                          {formatPct(row.percentage)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Section 3: Collection Efficiency ── */}
          <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden">
            <div className="p-6 pb-4 flex items-center gap-3 border-b border-gray-100">
              <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">3. Collection Efficiency</h2>
                <p className="text-xs text-gray-500">Demand raised vs amount actually recovered</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-6">
              <div className="bg-gradient-to-br from-white to-amber-50/30 p-5 rounded-2xl border border-amber-100">
                <span className="text-xs font-bold uppercase text-gray-500">Total Due</span>
                <div className="text-2xl font-black text-gray-900 mt-2">
                  {formatINR(collection.totalDue)}
                </div>
                <p className="text-xs text-gray-500 mt-1">EMI demand in the period</p>
              </div>

              <div className="bg-gradient-to-br from-white to-emerald-50/30 p-5 rounded-2xl border border-emerald-100">
                <span className="text-xs font-bold uppercase text-gray-500">Total Collected</span>
                <div className="text-2xl font-black text-emerald-700 mt-2">
                  {formatINR(collection.totalCollected)}
                </div>
                <p className="text-xs text-gray-500 mt-1">Recovered against demand</p>
              </div>

              <div className="bg-gradient-to-br from-white to-blue-50/30 p-5 rounded-2xl border border-blue-100">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase text-gray-500">Efficiency</span>
                  <div className="p-2 bg-blue-100 text-blue-600 rounded-xl">
                    <Percent className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-2xl font-black text-blue-700 mt-1">
                  {formatPct(collection.efficiency)}
                </div>
                <p className="text-xs text-gray-500 mt-1">Collected ÷ Due</p>
              </div>
            </div>

            <div className="px-6 pb-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">
                Monthly Collection Trend
              </h3>
              {collection.trend.length === 0 ? (
                <SectionEmpty text="No monthly collection trend returned for this period" />
              ) : (
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={collection.trend}
                      margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fill: "#6b7280", fontSize: 12 }} />
                      <YAxis
                        tickFormatter={(val) => `₹${val / 100000}L`}
                        tick={{ fill: "#6b7280", fontSize: 11 }}
                      />
                      <Tooltip
                        formatter={(value) => [formatINR(value), ""]}
                        contentStyle={{
                          borderRadius: "12px",
                          border: "1px solid #e5e7eb",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                        }}
                      />
                      <Legend iconType="circle" />
                      <Bar dataKey="due" fill="#cbd5e1" name="Total Due" radius={[6, 6, 0, 0]} />
                      <Bar dataKey="collected" fill={primaryColor} name="Total Collected" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* ── Section 4: Interest Income ── */}
          <div className="bg-white rounded-2xl border border-[#e5e7eb] shadow-sm overflow-hidden">
            <div className="p-6 pb-4 flex items-center gap-3 border-b border-gray-100">
              <div className="p-2 bg-purple-50 text-purple-600 rounded-xl">
                <Percent className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900">4. Interest Income</h2>
                <p className="text-xs text-gray-500">Interest earned on the lending book</p>
              </div>
            </div>

            <div className="p-6 pb-4">
              <div className="bg-gradient-to-br from-white to-purple-50/30 p-5 rounded-2xl border border-purple-100 max-w-sm">
                <span className="text-xs font-bold uppercase text-gray-500">Total Interest Earned</span>
                <div className="text-2xl font-black text-purple-700 mt-2">
                  {formatINR(interest.totalInterest)}
                </div>
                <p className="text-xs text-gray-500 mt-1">For the selected reporting period</p>
              </div>
            </div>

            <div className="px-6 pb-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">
                Month Wise Break-up
              </h3>
              {interest.rows.length === 0 ? (
                <SectionEmpty text="No month-wise interest break-up returned for this period" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50/75 text-gray-600 text-xs uppercase font-bold">
                        <th className="py-3.5 px-4">Month</th>
                        <th className="py-3.5 px-4 text-right">Interest Earned</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {interest.rows.map((row, idx) => (
                        <tr key={`${row.month}-${idx}`} className="hover:bg-gray-50/50 transition-colors">
                          <td className="py-3.5 px-4 font-semibold text-gray-800">{row.month}</td>
                          <td className="py-3.5 px-4 text-right font-black text-gray-900">
                            {formatINR(row.interest)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
